import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fireAutoOutreach(lead, count) {
  if (!lead?.id || lead.auto_outreach_ads_fired_at) return null;
  const st = (lead.crm_status || "NEW").toUpperCase();
  if (st !== "NEW" && st !== "CONTACTED") return null;
  try {
    const res = await axios.post(
      `${API}/outreach/leads/${lead.id}/auto-outreach-ads`,
      null,
      { params: { active_count: count } },
    );
    if (!res?.data?.skipped) {
      toast.success(`Auto-outreach fired — "${lead.name}" is running ${count} Meta ads`);
    }
    return res?.data;
  } catch (_e) { return null; }
}

/**
 * useMetaAdsBadges — per-lead Meta Ads "advertising now?" signal.
 *
 * Iter45: bulk cache-only lookup on mount + on-demand refresh(name,lead) per card.
 * Iter46: auto-fires a one-shot "competitor-advertising-on-Meta" cold email via
 *         POST /outreach/leads/{id}/auto-outreach-ads when badge shows
 *         has_active_ads:true for a NEW/CONTACTED lead that wasn't fired yet.
 *         Backend is idempotent (skips repeats via `auto_outreach_ads_fired_at`).
 *
 * Accepts either leadNames (string[]) or leads ({id,name,crm_status,...}[]).
 */
export function useMetaAdsBadges(leads, country = "US") {
  const [badges, setBadges] = useState({});
  // Track which lead ids we've already auto-fired against during this mount —
  // prevents racing refresh() and loadBulk() both dispatching for the same lead.
  const firedRef = useRef(new Set());

  // Accept either string[] or object[]; normalize internally.
  const leadObjs = Array.isArray(leads)
    ? leads.map((l) => (typeof l === "string" ? { name: l } : l)).filter((l) => l?.name)
    : [];
  const leadNames = leadObjs.map((l) => l.name);

  const loadBulk = useCallback(async (leadList) => {
    const uniqNames = Array.from(new Set(leadList.map((l) => l.name).filter(Boolean))).slice(0, 80);
    if (!uniqNames.length) return;
    try {
      const qs = uniqNames.map((n) => encodeURIComponent(n)).join("%7C");
      const { data } = await axios.get(
        `${API}/marketing-apis/meta/ad-library-bulk?queries=${qs}&country=${country}`,
      );
      const items = data?.items || {};
      setBadges((prev) => ({ ...prev, ...items }));
      // Iter46 — for each lead with a cache-hit showing active ads, auto-fire
      // the competitor-ads cold email (idempotent on the backend side too).
      leadList.forEach((lead) => {
        const hit = items[lead.name];
        if (hit && hit.has_active_ads && lead.id && !firedRef.current.has(lead.id)) {
          firedRef.current.add(lead.id);
          fireAutoOutreach(lead, hit.active_count || 0);
        }
      });
    } catch (_e) { /* silent — badges are best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBulk(leadObjs); }, [JSON.stringify(leadNames)]);

  const refresh = useCallback(async (name, lead) => {
    if (!name) return;
    setBadges((p) => ({ ...p, [name]: { ...(p[name] || {}), loading: true } }));
    try {
      const { data } = await axios.get(
        `${API}/marketing-apis/meta/ad-library`,
        { params: { q: name, country, force: true } },
      );
      const has = !!data?.has_active_ads;
      const count = data?.active_count || 0;
      setBadges((p) => ({
        ...p,
        [name]: { has_active_ads: has, active_count: count, cached: !!data?.from_cache },
      }));
      if (has && lead?.id && !firedRef.current.has(lead.id)) {
        firedRef.current.add(lead.id);
        fireAutoOutreach(lead, count);
      }
    } catch (_e) {
      setBadges((p) => ({ ...p, [name]: { ...(p[name] || {}), loading: false, error: true } }));
    }
  }, [country]);

  return { badges, refresh };
}
